import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { ReportColumn } from '../models/column.types'
import type { ReportResponse, ReportRow, ReportSpec } from '../models/report.types'
import {
  applyGa4Override,
  applyGa4OverrideTotal,
  getCompareOffsetDays,
  markGa4Columns,
  resolveGa4OverrideFields,
} from '../service/ga4Override'

import { i18n } from '@/shared/i18n'

export interface Ga4OverrideSource {
  rows: MaybeRefOrGetter<readonly ReportRow[]>
  response: MaybeRefOrGetter<ReportResponse | null | undefined>
  /** The request that produced `response`; its date/compare query string sets the compare offset. */
  settledSpec?: MaybeRefOrGetter<ReportSpec | null | undefined>
}

export interface UseGa4Override {
  rows: ComputedRef<ReportRow[]>
  total: ComputedRef<Record<string, unknown> | null>
  /** Override fields that took GA4 data in the rows or the total — the ones the dot marks. */
  fields: ComputedRef<string[]>
  /** Copies `columns`, marking the GA4 ones with the dot and the GA4 header tooltip (v3 `OrphexTh`). */
  markColumns: (columns: readonly ReportColumn[]) => ReportColumn[]
}

/**
 * GA4 override for a report surface (ORP-25549 / ORP-25673): rows of a date-only view take each
 * `ga4_total_override` field (and its `prev_*` baseline) from `data_ga4`, and the total takes them
 * from `total_ga4`. v3 parity: report tables and charts read `total_ga4` only; summing `data_ga4`
 * days into a total is the score cards' fallback.
 */
export function useGa4Override(
  source: Ga4OverrideSource,
  getColumn: (field: string) => ReportColumn | null | undefined,
): UseGa4Override {
  const response = computed(() => toValue(source.response))
  const fields = computed(() => resolveGa4OverrideFields(response.value, getColumn))
  const rowResult = computed(() =>
    applyGa4Override(toValue(source.rows), response.value?.data_ga4, {
      compareOffsetDays: getCompareOffsetDays(toValue(source.settledSpec)?.defaultPostData),
      fields: fields.value,
    }),
  )
  const totalResult = computed(() =>
    applyGa4OverrideTotal(response.value?.total ?? null, {
      totalGa4: response.value?.total_ga4,
      fields: fields.value,
    }),
  )
  const appliedFields = computed(() => [
    ...new Set([...rowResult.value.fields, ...totalResult.value.fields]),
  ])
  return {
    rows: computed(() => rowResult.value.rows),
    total: computed(() => totalResult.value.total),
    fields: appliedFields,
    markColumns: (columns) =>
      markGa4Columns(
        columns,
        appliedFields.value,
        [
          i18n.global.t('reports.ga4.title'),
          i18n.global.t('reports.ga4.body'),
          i18n.global.t('reports.ga4.tableSuffix'),
        ].join(' '),
      ),
  }
}
